import { format, formatDistanceToNow, formatDistanceStrict, formatDistance, parse } from 'date-fns'
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import fr from 'date-fns/locale/fr/index.js'

import { DateInputFormat, MerinioDateFormat } from '../types/dates'

type InputFormat = keyof DateInputFormat
export type OutputFormat = keyof MerinioDateFormat

type GetFormats = (options: { forceStandardTime?: boolean; standardTimeDateDisplay?: boolean }) => MerinioDateFormat

const getFormats: GetFormats = ({ forceStandardTime = false, standardTimeDateDisplay }): MerinioDateFormat => {
  // Adds handling for AM/Pm (standard time), you can also force standard
  const timeFormat = standardTimeDateDisplay || forceStandardTime ? 'h:mm a' : 'HH:mm'
  const timeCompleteFormat = standardTimeDateDisplay || forceStandardTime ? 'h:mm:ss a' : 'HH:mm:ss'

  return {
    date: `yyyy-MM-dd`,
    time: `${timeFormat}`,
    timeComplete: `${timeCompleteFormat}`,
    dateTime: `yyyy-MM-dd ${timeFormat}`,
    humanDate: {
      en: `EEE, MMM do yyyy`,
      fr: `EEE d MMM yyyy`
    },
    humanDateFull: {
      en: `EEEE, MMMM do yyyy`,
      fr: `EEEE d MMMM yyyy`
    },
    humanDateSemiCompact: {
      en: `EEE, MMM d yyyy`,
      fr: `EEE d MMM yyyy`
    },
    humanDateCompact: {
      en: `EEE, MMM do ''yy`,
      fr: `EEE d MMM ''yy`
    },
    humanDateTime: {
      en: `EEE, MMM do  yyyy (${timeFormat})`,
      fr: `EEE d MMM yyyy (${timeFormat})`
    },
    humanDateTimeEmphasisTime: {
      en: `${timeFormat} (EEE, MMM do ''yy)`,
      fr: `${timeFormat} (EEE d MMM ''yy)`
    },
    humanDateTimeLog: {
      en: `EEE, MMM do  yyyy (${timeCompleteFormat})`,
      fr: `EEE d MMM yyyy (${timeCompleteFormat})`
    },
    humanDateTimeFull: {
      en: `EEEE, MMMM do yyyy (${timeFormat})`,
      fr: `EEEE d MMMM yyyy (${timeFormat})`
    },
    humanDateTimeFullShift: {
      en: `EEEE, MMMM do yyyy ${timeFormat}`,
      fr: `EEEE d MMMM yyyy ${timeFormat}`
    },
    humanDateTimeCompact: {
      en: `MMM do ''yy (${timeFormat})`,
      fr: `d MMM ''yy (${timeFormat})`
    },
    tmpTranslatedDate: {
      en: `MMMM do`,
      fr: `d MMMM`
    },
    do: {
      en: `do`,
      fr: `d`
    },
    d: `d`,
    humanWeekDay: `eeee`,
    e: `e`, // (2, 3, 4, 5, ...)
    eeeee: `eeeee`, // (M, T, W, T, F,...)
    EEE: `EEE`, // (MON, TUE, WED, ...)
    MMM: `MMM`,
    monthAndYear: `MMMM yyyy`,
    year: `yyyy`,
    dayAndMonth: {
      en: `MMM do yyyy`,
      fr: `d MMM yyyy`
    },
    timeFull: `HH:mm:ss`,
    timeLog: `(${timeCompleteFormat})`,
    server: `yyyy-MM-dd'T'HH:mm:ss`,
    serverTime: `HH:mm`,
    serverTimeComplete: `HH:mm:ss`,
    flagDate: `yyyyMMdd`,
    distance: true,
    distanceStrict: true,
    distanceRelative: true,
    distanceRelativeStrict: true
  }
}

/**
 * Function to parse and format a provided date
 *
 * @param {string | Date} date
 * @param {object} options
 * @param {string} options.outFormat
 * @param {string} options.inFormat
 * @param {boolean} options.isUTC indicates that the date is utc
 * @param {string } options.timeZone user's timezone
 * @param {string } options.dateTimezone timezone that the original date is formatted in, will trigger a timezone conversion if present with timeZone param
 * @param {locale} options.locale allows to overwrite the translation locale
 *
 * @return returns a formated (and translated) date
 */

type FormatOptions = {
  outFormat?: OutputFormat
  inFormat?: InputFormat
  isUTC?: boolean
  timeZone?: string
  dateTimezone?: string
  locale?: Locale
  forceStandardTime?: boolean
  relativeDate?: Date | string
  standardTimeDateDisplay?: boolean
  currentLang?: 'fr' | 'en'
}

type FormatType = (input: string | Date, options?: FormatOptions) => string | undefined

const Format: FormatType = (input, options) => {
  const {
    isUTC,
    outFormat = `server`,
    inFormat = `server`,
    timeZone,
    dateTimezone,
    relativeDate,
    locale,
    forceStandardTime,
    standardTimeDateDisplay,
    currentLang
  } = options || {}
  // handle edge case where date does not exist (avoids a lot of crashes)
  let localLanguage = locale
  if (!input) return ''

  // Don't modify the original input
  const date = input

  const formats = getFormats({ forceStandardTime, standardTimeDateDisplay })

  // This helps prevent throwing errors when trying to parse a date that can sometimes be undefined
  // used to simplify some translations.
  if (!date) return ''
  if (!formats[inFormat]) throw new Error(`Invalid input date format (${inFormat}) for parser. Please use a valid format`)
  if (!formats[outFormat]) throw new Error(`Invalid output date format (${outFormat}) for formatter. Please use a valid format`)

  if (!locale) localLanguage = currentLang === 'en' ? undefined : fr
  const lang = currentLang === 'en' ? 'en' : 'fr'

  // Build the options to send to the parse function
  const parseOptions = { inFormat, forceStandardTime }

  // If the date is a string, parse it
  // Remove some parseOptions like isUTC to avoid UTC twice
  let parsedDate = date instanceof Date ? date : Parse(date, parseOptions)

  // If this option is set, convert the date from the original timezone to the user's timezone
  if (dateTimezone && timeZone) parsedDate = convertBetweenTimezones(parsedDate, dateTimezone, timeZone)

  // If utc is set convert from utc to the user's timezone
  if (isUTC && timeZone && parsedDate) parsedDate = utcToTimeZone(parsedDate, timeZone)

  // Relative formatting options
  if (outFormat === 'distance' && parsedDate) return formatDistanceToNow(parsedDate, { locale: localLanguage, addSuffix: true })
  if (outFormat === 'distanceStrict' && parsedDate) return formatDistanceStrict(new Date(), parsedDate, { locale: localLanguage })
  if (outFormat === 'distanceRelative' && parsedDate && relativeDate)
    return formatDistance(parsedDate, Parse(relativeDate, parseOptions), { locale: localLanguage })
  if (outFormat === 'distanceRelativeStrict' && parsedDate && relativeDate)
    return formatDistanceStrict(parsedDate, Parse(relativeDate, { inFormat }), { locale: localLanguage })

  // Sets the langauge format if it exists, if not set the
  const dateformat = formats[outFormat][lang] ? formats[outFormat][`${lang}`] : formats[`${outFormat}`]

  try {
    return format(parsedDate, dateformat, { locale: localLanguage })
  } catch (err) {
    console.info({ input, options })
    throw err
  }
}

/**
 * Function to parse a string into a Date
 *
 * @param {string | Date} date
 * @param {string} inFormat
 *
 * @return returns a parsed instance of Date
 */

type ParseFunc = (
  input: string | Date,
  options?: { inFormat?: InputFormat; forceStandardTime?: boolean; timeZone?: string; isUTC?: boolean; standardTimeDateDisplay?: boolean }
) => Date

const Parse: ParseFunc = (date, options) => {
  const { inFormat = 'server', forceStandardTime, standardTimeDateDisplay } = options || {}

  const formats = getFormats({ forceStandardTime, standardTimeDateDisplay })

  // Handle case where date is undefined

  if (!formats[inFormat]) throw new Error(`Invalid input date format (${inFormat}) for parser. Please use a valid format`)
  // If the date provided is already a date just return it, no need to parse it
  if (date instanceof Date) return date
  let parsedDate = parse(date, formats[inFormat], new Date())

  // Some field do not have a standardized format (could be YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss) the app crashes because of this.
  // We then try to parse the different formats if the provided result was 'Invalid Date'
  if (parsedDate.toString() === 'Invalid Date') parsedDate = parse(date, formats[`date`], new Date())
  if (parsedDate.toString() === 'Invalid Date') parsedDate = parse(date, formats[`server`], new Date())
  // Arrival / departure time can be stored in format HH:mm or HH:mm:ss
  if (inFormat === 'serverTime' && parsedDate.toString() === 'Invalid Date') parsedDate = parse(date, formats[`serverTimeComplete`], new Date())

  return parsedDate
}

// Takes a string or Date object, parses it to valid utc iso string, then to formatted string in passed time zone
const utcToTimeZone = (date: string | Date, timeZone: string) => {
  const formats = getFormats({})
  if (typeof date !== 'string') date = format(date, formats.server)
  return utcToZonedTime(date + 'Z', timeZone)
}

type ConvertToUserTime = (date: Date, dateTimezone: string, timeZone: string) => Date

const convertBetweenTimezones: ConvertToUserTime = (date, dateTimezone, timeZone) => {
  // Skip this if no conversion is required
  if (timeZone === dateTimezone) return date
  // Get the utc version of the date
  const utcDate = zonedTimeToUtc(date, dateTimezone)
  // Convert the utc version to the user's timezone
  // TODO: There should be a user setting for this (use company for default)
  return utcToZonedTime(utcDate, timeZone)
}

export { Format, Parse }
